import { ROUTINE_BUBBLE_SCALE, ROUTINE_ORBIT_GAP, fitRoutineNodeOrbit } from '../../../../engine/core/routine-orbit.js';
import { appendBadge as appendRoutineBadge, badgeMarkup as routineBadgeMarkup, isRoutine } from '../../ui/routines.js';
/**
 * Bubble View Render Module
 * Handles DOM rendering, bubble creation, and visual updates
 */

import { getBubblePositions } from "../../store/bubble-positions.js";
import * as Store from "../../store/index.js";
import { safeColor, escapeAttr } from "../../../../engine/core/validation.js";
import { setupNotePreviewOnBadge } from "../../ui/note-preview.js";
import { getLinkedBadge, linkedStubContainerClasses } from "../linked-badge.js";
import { isMobile } from "../../../../shared/utils/device-detect.js";
import * as LabelLOD from "./label-lod.js";
import * as Titles from "./titles.js";
import { renderHorizonControl } from "../../ui/horizon-control.js";
import { t } from "../../../../shared/i18n/index.js";
import { viewLabel } from "../../../../engine/core/view-names.js";
import { titleBand, titleFontSize, titleArcLength, shapeOverlap, escapeDistance, SIBLING_GAP } from "./layout.js";
import { fitArcTitle } from "./canvas-text.js";

// ============================================================================
// CACHES
// ============================================================================

const sizeCache = new Map();
// Memoized "is this node visible" lookups. Built lazily during a render and
// cleared between renders. See makeIsVisible() for the visibility rules.
const visibilityCache = new Map();
// Set of node ids that are not themselves a tag-match but have a tag-match
// somewhere in their subtree. Used to keep ancestors un-dimmed in spotlight
// mode so the user can visually trace down to matching descendants. Built
// once per render in renderBubbles().
const tagMatchAncestorIds = new Set();
// Parent id -> spacing of the start spiral of its fresh children (per render).
const spiralSpacingCache = new Map();
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

// ============================================================================
// CACHE MANAGEMENT
// ============================================================================

export function clearCaches() {
  sizeCache.clear();
  spiralSpacingCache.clear();
  visibilityCache.clear();
  tagMatchAncestorIds.clear();
  tagFilterCache = null;
}

// The render pass's tag filter (engine/core/tag-filter.js), set by renderBubbles.
let tagFilterCache = null;
function tagFilter() {
  return tagFilterCache;
}

// ============================================================================
// BUBBLES KEPT BETWEEN RENDERS
// ============================================================================

// A change to the map's structure rebuilt every bubble: an item added to a
// map of 20,000 items waited about 0.7 s before its motion, most of it
// building, styling and laying out 20,000 elements again. A render now keeps
// each element whose content is unchanged (bubbleContent) where it is, sets
// its size, and builds only new or changed bubbles. Listeners read the
// bubble's current data from its element (`el._pNode`).
let lastRenderContext = null;

/** Forget the kept bubbles (the view is destroyed). */
export function forgetBubbles() {
  lastRenderContext = null;
}

/**
 * The previous render's bubbles that this one may keep, by id. None when what
 * every bubble depends on changed: the world element (the view was rebuilt),
 * the map, its key (note previews decrypt with it), or read-only.
 */
function keptBubbles(physicsNodes, world, dragLayer, docId, callbacks) {
  const context = {
    world,
    docId,
    noteDocId: callbacks?.getDocId?.() ?? null,
    dek: callbacks?.getDek?.() ?? null,
    readOnly: !!callbacks?.isReadOnly?.(),
  };
  const previous = lastRenderContext;
  lastRenderContext = context;
  const kept = new Map();
  if (!previous || Object.keys(context).some((key) => previous[key] !== context[key])) return kept;
  for (const n of physicsNodes) {
    // A bubble being dragged (in the drag layer) is built again, as before.
    if (n.el?._content && n.el.isConnected && n.el.parentElement !== dragLayer) kept.set(n.id, n);
  }
  return kept;
}

/**
 * What a bubble's element shows, apart from its size and place: a render
 * keeps an element built from the same content.
 */
function bubbleContent(node, isLeaf, status, spotlight, routine, bodySize) {
  return {
    isLeaf,
    hasChildren: node.children?.length > 0,
    collapsed: !!node.collapsed,
    status: status || null,
    color: node.color,
    name: node.name || node.text || "",
    notes: node.notes && node.notes.trim() ? node.notes : "",
    reference: !!node._isReferenceNode,
    linked: node.linkedDocId
      ? `${node.linkedDocId}\n${node._linkedState}\n${!!node._linkedDocLoaded}\n${node.linkedDocTitle}`
      : "",
    routine: routine ? `${routineBadgeMarkup(node.id)}\n${bodySize}` : "",
    spotlight,
  };
}

function sameContent(a, b) {
  if (!a) return false;
  for (const key in b) if (a[key] !== b[key]) return false;
  return true;
}

/**
 * Build a memoized "is visible" predicate. Two rules compose, in priority
 * order:
 *
 *   1. DONE-CASCADE. If Done is currently hidden (legacy hideCompleted=true
 *      OR the Done tag id is in hiddenTagIds), a Done node hides its entire
 *      subtree — descendants don't get a chance to override. Lets the user
 *      "close a topic" by marking the parent Done.
 *
 *   2. ANCESTOR-AWARE for everything else. A node hidden by some other tag
 *      stays visible if it has any visible descendant, so the path to a
 *      matching leaf is reachable through an otherwise-filtered ancestor.
 *
 * @param {Object} currentState
 * @param {string[]} hiddenTagIds
 * @param {Function|null} nodeIsDoneFn  - null-safe; falls back to legacy `status==="done"` only
 * @param {string|null} doneTagId       - id of the Done mandatory tag, or null if unknown
 * @param {Function|null} outOfHorizon   - horizon cut-off predicate (engine/core/horizon.js), or null when no horizon is set
 * @returns {(node: Object) => boolean}
 */
function makeIsVisible(currentState, hiddenTagIds, nodeIsDoneFn, doneTagId, outOfHorizon = null) {
  const hide = !!currentState?.hideCompleted;
  const hasHiddenTags = hiddenTagIds && hiddenTagIds.length > 0;
  if (!hide && !hasHiddenTags && !outOfHorizon) return () => true;

  const doneHidden = hide || (!!doneTagId && hiddenTagIds.includes(doneTagId));
  const isDone = (n) => n.status === "done" || !!nodeIsDoneFn?.(n);

  const isSelfHidden = (n) => {
    if (hide && isDone(n)) return true;
    if (outOfHorizon && outOfHorizon(n)) return true;
    if (tagFilter()?.isHidden(n, hiddenTagIds)) return true;
    return false;
  };

  const isVisible = (n) => {
    if (visibilityCache.has(n.id)) return visibilityCache.get(n.id);
    let visible;
    if (doneHidden && isDone(n)) {
      // Cascade: Done node with Done hidden disappears with all its subtree.
      visible = false;
    } else if (!isSelfHidden(n)) {
      visible = true;
    } else {
      // Self-hidden by a non-Done filter — keep the parent visible if any
      // descendant survives, so the path to that descendant stays open.
      visible = (n.children || []).some(isVisible);
    }
    visibilityCache.set(n.id, visible);
    return visible;
  };
  return isVisible;
}

/**
 * Compute the set of node ids whose subtree contains a tag-match but which
 * are NOT themselves a tag-match. Mutates `tagMatchAncestorIds` in place.
 *
 * @param {Object} root
 * @param {string[]} highlightedTagIds
 */
function rebuildTagMatchAncestorIds(root, highlightedTagIds) {
  // No internal clear here — clearCaches() (called at the top of every
  // renderBubbles) already empties the set. Adding a redundant clear would
  // only mask a misuse where this is called outside a render.
  if (!root || !highlightedTagIds?.length) return;
  const isMatch = (n) => !!tagFilter()?.isMatch(n, highlightedTagIds);
  // Returns true iff the subtree rooted at `n` contains any match.
  const visit = (n) => {
    let any = isMatch(n);
    for (const c of n.children || []) {
      if (visit(c)) any = true;
    }
    if (any && !isMatch(n)) tagMatchAncestorIds.add(n.id);
    return any;
  };
  visit(root);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================


/**
 * Get contrasting text color for a background
 * @param {string} hexColor - Background color in hex format
 * @returns {string} Contrasting text color
 */
/** Saved position of a bubble, if it was saved under this parent (coordinates are parent-relative). */
function savedPositionIn(savedPositions, id, parentId) {
  const pos = savedPositions[id];
  return Number.isFinite(pos?.x) && Number.isFinite(pos?.y) &&
    (pos.parentId === undefined || pos.parentId === parentId) ? pos : null;
}

/**
 * Space a parent bubble's title takes, for the layout (layout.js): the title
 * as titles.js draws it, in the map's units. The mobile canvas draws a
 * smaller straight title above the bubble, which fits in the same band.
 */
function titleBandFor(name, size, textRadius) {
  if (!name) return null;
  const fontSize = titleFontSize(size);
  return titleBand(textRadius, fontSize, fitArcTitle(name, fontSize, titleArcLength(textRadius)).width);
}

export function getContrastColor(hexColor) {
  if (!hexColor) return "#333";
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#333" : "#fff";
}

/**
 * Diameter a bubble takes in its parent's layout: a parent bubble also needs
 * room for its title band above it (layout.js keeps titles apart and inside
 * the parent).
 */
// Fraction of a circle that n equal circles fill at best (known optimal
// packings); more circles tend to about 0.78. The estimate allows 10% more
// room: the simulation settles a little looser than the optimum.
const PACKING = [1, 1, 0.5, 0.646, 0.686, 0.685, 0.667, 0.778, 0.733, 0.689, 0.687, 0.714, 0.739];
function packingFactor(n) {
  return 1 / Math.sqrt(0.9 * (n < PACKING.length ? PACKING[n] : 0.78));
}

function layoutSize(node, size) {
  return node.children?.length && !node.collapsed ? size + 4 + titleFontSize(size) : size;
}

/**
 * Calculate bubble size based on descendants (area-based)
 * Uses memoization to avoid redundant calculations
 * Respects hideCompleted setting - only counts visible children
 * @param {Object} node - Node to calculate size for
 * @param {boolean} hideCompleted - Whether to hide completed nodes
 * @param {number} minSize - Minimum bubble size
 * @param {number} padding - Padding around bubbles
 * @returns {number} Calculated size
 */
export function calcSize(node, hideCompleted, minSize, padding, hiddenTagIds = null, nodeIsDoneFn = null, isVisibleFn = null) {
  const ownMinSize=isRoutine(node.id)?minSize*ROUTINE_BUBBLE_SCALE:minSize;
  if (!node.children?.length || node.collapsed) return ownMinSize;

  // Check cache first
  const cached = sizeCache.get(node.id);
  if (cached !== undefined) return cached;

  // Use the ancestor-aware visibility predicate when one is supplied (the
  // top-level renderBubbles call builds it once per render). Falls back to
  // the legacy per-node check for any caller that hasn't been updated.
  const isVisible = isVisibleFn || ((child) => {
    if (hideCompleted && (child.status === "done" || nodeIsDoneFn?.(child))) return false;
    if (tagFilter()?.isHidden(child, hiddenTagIds)) return false;
    return true;
  });
  const visibleChildren = node.children.filter(isVisible);

  // If no visible children, return minimum size
  if (visibleChildren.length === 0) return ownMinSize;

  const childSizes = visibleChildren.map((c) => layoutSize(c, calcSize(c, hideCompleted, minSize, padding, hiddenTagIds, nodeIsDoneFn, isVisibleFn)));
  const totalArea = childSizes.reduce((s, r) => s + Math.PI * r * r, 0);
  const base = Math.sqrt(totalArea / Math.PI);
  const size = Math.max(
    minSize * 1.1,
    base * packingFactor(childSizes.length) + Math.min(padding, base * 0.06),
  ) * (isRoutine(node.id)?ROUTINE_BUBBLE_SCALE:1);

  sizeCache.set(node.id, size);
  return size;
}

// ============================================================================
// MAIN RENDER FUNCTION
// ============================================================================

/**
 * Render bubbles for the focused node
 * Uses document fragments for smoother DOM updates (reduces flicker)
 * @param {Object} options - Render options
 * @param {Object} options.state - State module accessors
 * @param {Object} options.physics - Physics module accessors
 * @param {Function} options.setupBubbleDrag - Drag setup function
 * @param {Function} options.zoomTo - Zoom function
 */
export function renderBubbles(options) {
  const {
    state,
    physics,
    setupBubbleDrag,
    zoomTo,
    canvasMode = false,
  } = options;

  const world = state.getWorld();
  const canvas = state.getCanvas();
  const dragLayer = state.getDragLayer();
  const currentState = state.getCurrentState();
  const currentDocId = state.getCurrentDocId();
  const callbacks = state.getCallbacks();
  const container = state.getContainer();
  const minSize = state.MIN_SIZE;
  const padding = state.PADDING;

  if (!world) return;

  // Tag spotlight mode: toggle class on canvas (parent of the world; titles.js reads it too)
  const spotlightTagIds = callbacks?.getHighlightedTagIds?.() || [];
  canvas?.classList.toggle('tag-filtering', spotlightTagIds.length > 0);

  // Save current physics positions before clearing DOM
  // This preserves positions when re-rendering mid-physics (e.g., when adding a node)
  const physicsNodes = physics.getPhysicsNodes();
  if (physicsNodes.length > 0) {
    physics.saveAllPositions(currentDocId);
  }

  // Clear caches for fresh calculations
  clearCaches();
  tagFilterCache = callbacks?.makeTagFilter?.() || null;

  // Prepare new physics nodes array
  const newPhysicsNodes = [];

  const focused = callbacks.find(currentState.focusId) || currentState.data;
  state.setFocused(focused);

  if (!focused) {
    world.innerHTML = "";
    if (dragLayer) dragLayer.innerHTML = "";
    physics.setPhysicsNodes([]);
    return;
  }

  renderBreadcrumbs({
    container,
    currentState,
    callbacks,
    zoomTo,
  });

  const rect = canvas.getBoundingClientRect();
  const cx = rect.width / 2;
  const cy = rect.height / 2;

  if (!focused.children?.length) {
    // Position empty message at canvas center
    world.innerHTML = `<div class="empty" style="position:absolute;left:${cx}px;top:${cy}px;transform:translate(-50%,-50%)"><div class="empty-title">${t("views.noItems")}</div><div style="font-size:13px">${t("views.bubble.emptyHint")}</div></div>`;
    if (dragLayer) dragLayer.innerHTML = "";
    physics.setPhysicsNodes([]);
    state.updateTransform();
    return;
  }

  // Safety check for canvas dimensions
  if (!rect.width || !rect.height || rect.width < 10 || rect.height < 10) {
    const retryCount = state.getRetryCount();
    state.setRetryCount(retryCount + 1);
    if (retryCount < 30) {
      setTimeout(() => renderBubbles(options), 100);
      return;
    } else {
      world.innerHTML = `<div class="empty"><div class="empty-title">${t("views.bubble.canvasError")}</div><div style="font-size:13px">${t("views.bubble.refresh")}</div></div>`;
      state.setRetryCount(0);
      return;
    }
  }
  state.setRetryCount(0);

  // Build the ancestor-aware visibility predicate ONCE for this render. Used
  // both at the top-level filter below and inside calcSize / recursive child
  // filters via the options passed through createBubbleRecursive.
  const bubbleHiddenTagIds = callbacks?.getHiddenTagIds?.() || [];
  const doneTagId = callbacks?.getDoneTagId?.() || null;
  const outOfHorizon = callbacks?.getHorizonPredicate?.() || null;
  const isVisibleFn = makeIsVisible(currentState, bubbleHiddenTagIds, callbacks?.nodeIsDone, doneTagId, outOfHorizon);

  // And precompute which non-matching nodes have a tag-match in their
  // subtree, so they get a `tag-match-ancestor` class and stay un-dimmed in
  // spotlight mode (keeps the visual path to the matching descendant).
  rebuildTagMatchAncestorIds(focused, spotlightTagIds);

  const childrenToShow = focused.children.filter(isVisibleFn);

  // The previous render's bubbles to keep (none in canvas mode, or for a
  // first render: then everything is built off screen and added at once).
  const kept = canvasMode ? new Map() : keptBubbles(physicsNodes, world, dragLayer, currentDocId, callbacks);
  const keeping = kept.size > 0; // the map empties as bubbles are kept
  const placed = new Set(); // this render's bubble elements, kept or new

  // Build new DOM in document fragments (off-screen) to reduce flicker
  const worldFragment = keeping ? world : document.createDocumentFragment();

  // Build bubbles into fragments
  // Read one snapshot after saving the live physics positions above. Reading
  // one position per node parsed the entire document's position JSON each time
  // (quadratic work). Keep this local to the render so tab/doc changes and
  // writes from another window are still picked up on the next render.
  const savedPositions = getBubblePositions(currentDocId);
  const tempPhysicsNodes = [];
  childrenToShow.forEach((child) => {
    createBubbleRecursive(
      child,
      worldFragment,
      null,
      cx,
      cy,
      tempPhysicsNodes,
      {
        state,
        physics,
        setupBubbleDrag,
        zoomTo,
        focused,
        minSize,
        padding,
        isVisibleFn,
        canvasMode,
        savedPositions,
        kept,
        placed,
      },
    );
  });

  if (keeping) {
    // Kept bubbles were moved into their new parents as needed: what is left
    // of the previous render goes (anything else in the world too, such as
    // the empty map's message).
    for (const n of physicsNodes) {
      if (n.el && !placed.has(n.el)) n.el.remove();
    }
    for (const el of [...world.children]) {
      if (!placed.has(el)) el.remove();
    }
  } else {
    // Swap DOM in single operations (reduces visible flicker)
    world.innerHTML = "";
    world.appendChild(worldFragment);
  }

  if (dragLayer) {
    dragLayer.innerHTML = "";
  }

  // Update physics nodes array
  physics.setPhysicsNodes(tempPhysicsNodes);

  // Build parent/children links here, not only inside startPhysics: the drag
  // interaction walks `children` to move descendant labels, and it must work
  // on renders where the simulation below is skipped.
  tempPhysicsNodes.forEach((n) => {
    n.children = [];
  });
  tempPhysicsNodes.forEach((n) => {
    if (n.parent) n.parent.children.push(n);
  });

  // Initial position update BEFORE physics starts
  tempPhysicsNodes.forEach((n) => {
    physics.updateBubblePos(n);
  });

  // Label LOD cache: elements were just recreated, so rebuild it. In canvas
  // mode there are no DOM labels — clear so stale entries can't linger.
  if (canvasMode) {
    LabelLOD.clear();
  } else {
    LabelLOD.rebuild(tempPhysicsNodes, state.getCanvas(), { width: rect.width, height: rect.height });
    Titles.schedule();
  }

  // Skip the simulation when the saved layout is already valid: every node
  // has a stored position and no sibling pair overlaps (more than the saved
  // layout already did) or escapes its parent. Previously physics ran on EVERY render — a re-focus, tag change
  // or fold on a settled 715-node map burned seconds of rAF frames arriving
  // at the positions it already had. Radius changes (fold/unfold growing a
  // parent) surface as overlaps here, so those still simulate.
  const dirtyIds = collectDirtyPhysicsIds(tempPhysicsNodes);
  if (dirtyIds.length) {
    // A fit waiting for this layout frames the bubbles' first places now, so
    // the layout animates what will be seen (index.js beforeLayout).
    options.beforeLayout?.();
    physics.startPhysics({
      currentDocId,
      dirtyIds,
      onFinish: options.onLayoutSettled,
      scheduleRender: () => renderBubbles(options),
    });
  }

  state.updateTransform();

  // Update structure hash after render to track current state
  state.setLastTreeStructureHash(
    state.computeTreeStructureHash(focused, currentState.hideCompleted)
  );
}

/**
 * Property-only changes (name, color, tag-derived status) refresh the display
 * data carried on the physics nodes, which the phone's canvas and the titles
 * (titles.js) are drawn from. Structural changes go through the full rebuild
 * as usual.
 *
 * @param {Object} rootNode - New tree root
 * @param {Object} state - State accessor (for callbacks)
 * @param {Array} physicsNodes - Live physics nodes
 */
export function refreshDisplayData(rootNode, state, physicsNodes) {
  if (!rootNode || !physicsNodes?.length) return;
  const callbacks = state.getCallbacks();

  const nodeMap = new Map();
  (function build(node) {
    if (!node) return;
    nodeMap.set(node.id, node);
    node.children?.forEach(build);
  })(rootNode);

  for (const pn of physicsNodes) {
    const node = nodeMap.get(pn.id);
    if (!node) continue;
    pn.dataNode = node;
    pn.name = node.name || node.text || "";
    pn.color = node.color;
    pn.status = callbacks?.getEffectiveStatus?.(node) || node.status || null;
  }
}

/**
 * Find layout changes: fresh/reparented positions, changed radii, children
 * (or their titles) outside their parent, and overlapping siblings (titles
 * included). Only these nodes and their sibling groups up the ancestor chain
 * need to move.
 *
 * The physics enforces these constraints with a gap to spare, so a settled
 * layout passes; the 1px tolerance absorbs rounding and font-metric noise.
 * A crowded parent can leave an overlap the layout cannot resolve: saved
 * with the positions (`o`), it is accepted, so the map does not simulate
 * again on every opening.
 * Layouts saved before titles counted are repaired once.
 * @param {Array} nodes - Physics nodes
 * @returns {Array<string>} Node IDs needing layout
 */
function collectDirtyPhysicsIds(nodes) {
  const dirty = new Set();
  const groups = new Map(); // parent id (or "") -> siblings
  for (const n of nodes) {
    if (
      !n.manuallyPositioned ||
      !Number.isFinite(n.savedRadius) ||
      Math.abs(n.savedRadius - n.radius) > 1 ||
      (n.parent && escapeDistance(n, n.parent) > 1)
    ) {
      dirty.add(n.id);
    }
    const key = n.parent ? n.parent.id : "";
    let g = groups.get(key);
    if (!g) groups.set(key, (g = []));
    g.push(n);
  }
  for (const group of groups.values()) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i];
        const b = group[j];
        if (shapeOverlap(a, b) > 1 + Math.max(a.savedOverlap || 0, b.savedOverlap || 0)) {
          dirty.add(a.id);
          dirty.add(b.id);
        }
      }
    }
  }
  return [...dirty];
}

// ============================================================================
// RECURSIVE BUBBLE CREATION
// ============================================================================

/**
 * Create a bubble and its children recursively
 * @param {Object} node - Data node to render
 * @param {HTMLElement|DocumentFragment} parentContainer - Parent container for bubble
 * @param {Object|null} parentPhys - Parent physics node
 * @param {number} offsetX - X offset
 * @param {number} offsetY - Y offset
 * @param {Array} physicsArray - Array to collect physics nodes
 * @param {Object} options - Additional options
 */
function createBubbleRecursive(
  node,
  parentContainer,
  parentPhys,
  offsetX,
  offsetY,
  physicsArray,
  options,
) {
  const {
    state,
    physics,
    setupBubbleDrag,
    zoomTo,
    focused,
    minSize,
    padding,
    isVisibleFn,
  } = options;

  const currentState = state.getCurrentState();
  const currentDocId = state.getCurrentDocId();
  const callbacks = state.getCallbacks();

  const hiddenTagIds = callbacks?.getHiddenTagIds?.() || [];
  const nodeIsDoneFn = callbacks?.nodeIsDone || null;
  const size = calcSize(node, currentState.hideCompleted, minSize, padding, hiddenTagIds, nodeIsDoneFn, isVisibleFn);
  const routine = isRoutine(node.id);
  const bodySize = routine ? size / ROUTINE_BUBBLE_SCALE : size;
  const hasChildren = node.children?.length > 0;
  // Read, never written here: the map's items belong to the engine (a
  // missing value means not folded).
  const isLeaf = !hasChildren || !!node.collapsed;
  // Parent bubbles carry a curved title on the top arc, outside the rim.
  const titleRadius = (routine ? bodySize / 2 + ROUTINE_ORBIT_GAP : size / 2) + 4;

  // Check read-only status once for this node
  const isViewer = callbacks.isReadOnly && callbacks.isReadOnly();

  const bubbleStatus = callbacks?.getEffectiveStatus?.(node) || node.status;

  // Tag spotlight resolved as data first — the DOM path turns it into CSS
  // classes below, the canvas path reads it directly to dim non-matches.
  const highlightedTagIds = callbacks?.getHighlightedTagIds?.() || [];
  let spotlight = null; // 'match' | 'ancestor' | 'dim' | null
  if (highlightedTagIds.length) {
    const hasMatch = !!tagFilter()?.isMatch(node, highlightedTagIds);
    spotlight = hasMatch ? "match" : tagMatchAncestorIds.has(node.id) ? "ancestor" : "dim";
  }

  // Canvas mode (mobile): no DOM at all — the physics node carries the
  // display data and canvas-renderer.js paints it each frame. Everything
  // outside this guard (sizing, saved positions, physics wiring, recursion)
  // is shared between the two render paths.
  let bubble = null;
  // Kept for label-lod.js, which would otherwise search the DOM for it.
  let centeredLabel = null;
  // The previous render's bubble, kept when its content is unchanged (only
  // its size is set); otherwise built.
  const previous = options.kept?.get(node.id);
  const content = options.canvasMode ? null : bubbleContent(node, isLeaf, bubbleStatus, spotlight, routine, bodySize);
  const keep = !!previous && sameContent(previous.el._content, content);
  if (keep) {
    options.kept.delete(node.id); // an id shown twice gets a second element
    bubble = previous.el;
    centeredLabel = previous.centeredLabel;
    if (bubble._size !== size) {
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      bubble._size = size;
    }
    // Into its new parent before its children are placed: parents come
    // first, so the new parent is already where it goes, even when it was
    // inside this bubble before (a parent moved into its former child).
    if (bubble.parentElement !== parentContainer) parentContainer.appendChild(bubble);
  }
  if (!options.canvasMode && !keep) {
  bubble = document.createElement("div");
  bubble._content = content;
  bubble._size = size;
  bubble.className = isLeaf ? "bubble leaf" : "bubble";

  // Add collapsed class for CSS hover behavior
  if (hasChildren && node.collapsed) {
    bubble.classList.add("collapsed");
  }

  bubble.dataset.id = node.id;
  if (bubbleStatus) {
    bubble.dataset.status = bubbleStatus;
  }
  if (node.id === currentState.selectedId) bubble.classList.add("selected");

  // Style the bubble
  if (isLeaf) {
    const textColor = getContrastColor(node.color);
    bubble.style.cssText = `
      width: ${size}px;
      height: ${size}px;
      outline-color: ${safeColor(node.color)};
      --bubble-fill-color: ${safeColor(node.color)};
      --bubble-text-color: ${textColor};
    `;
  } else {
    bubble.style.cssText = `
      width: ${size}px;
      height: ${size}px;
      outline-color: ${safeColor(node.color)};
      --bubble-ring-color: ${safeColor(node.color)};
    `;
  }

  if (routine) {
    bubble.style.setProperty('--routine-body-size', `${bodySize}px`);
    const body = document.createElement('span');
    body.className = 'routine-node-body';
    body.setAttribute('aria-hidden', 'true');
    bubble.appendChild(body);
  }

  // Tag spotlight: mark matching nodes so non-matching ones can be dimmed via CSS.
  // `tag-match-ancestor` keeps ancestors of a match un-dimmed too — without it
  // the dim overlay would block the visual path from focus down to the match.
  if (spotlight === "match") bubble.classList.add("tag-match");
  else if (spotlight === "ancestor") bubble.classList.add("tag-match-ancestor");

  // Add label and badges for leaf nodes
  if (isLeaf) {
    // Container for text and badges (stacked vertically)
    const labelContainer = document.createElement("div");
    labelContainer.className = "bubble-label-centered";
    centeredLabel = labelContainer;

    // Text label
    const label = document.createElement("span");
    label.className = "bubble-leaf-label";
    label.textContent = node.name || node.text || "";
    // Long names are cut at the end (CSS); the full name shows on hover.
    label.title = label.textContent;
    label.addEventListener("dblclick", (e) => {
      e.stopPropagation();
      // Block inline editing for read-only viewers
      if (isViewer) return;
      if (callbacks.onNodeEdit) {
        callbacks.onNodeEdit(node.id, e.clientX, e.clientY);
      }
    });
    labelContainer.appendChild(label);

    // Badge container (below text)
    const hasBadges = hasChildren || (node.linkedDocId || node._isReferenceNode) || (node.notes && node.notes.trim());
    if (hasBadges) {
      const badgeContainer = document.createElement("div");
      badgeContainer.className = "bubble-badge-container";

      // Reference node badge — distinct icon + state via shared helper.
      const linked = getLinkedBadge(node);
      if (linked.isLinkedStub) {
        const refBadge = document.createElement("span");
        refBadge.className = `bubble-inline-badge ref ${linked.className}`;
        refBadge.textContent = linked.icon;
        refBadge.title = linked.title;
        badgeContainer.appendChild(refBadge);
        bubble.classList.add("reference-node");
        const stubClasses = linkedStubContainerClasses(node);
        if (stubClasses) bubble.className += ` ${stubClasses}`;
      }

      // Notes badge - shows if node has notes
      if (node.notes && node.notes.trim()) {
        const notesBadge = document.createElement("span");
        notesBadge.className = "bubble-inline-badge notes";
        notesBadge.textContent = "\uD83D\uDDD2\uFE0F";
        // Hover preview is desktop-only.
        if (!isMobile()) {
          setupNotePreviewOnBadge(notesBadge, node.notes, callbacks?.getDocId?.(), callbacks?.getDek?.());
        }
        notesBadge.addEventListener("click", (e) => {
          e.stopPropagation();
          if (callbacks.onOpenModal) {
            callbacks.onOpenModal(node.id);
          }
        });
        badgeContainer.appendChild(notesBadge);
      }

      // Toggle badge - for collapsed nodes with children
      // Hide for read-only viewers
      if (hasChildren && !isViewer) {
        const toggleBtn = document.createElement("span");
        toggleBtn.className = "bubble-inline-badge toggle";
        toggleBtn.textContent = node.collapsed ? "+" : "\u2212";
        toggleBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          const current = bubble._pNode.dataNode;
          current.collapsed = !current.collapsed;
          bubble.classList.toggle("collapsed", current.collapsed);
          toggleBtn.textContent = current.collapsed ? "+" : "\u2212";
          if (callbacks.onUpdateNode) {
            callbacks.onUpdateNode(node.id, { collapsed: current.collapsed });
          }
        });
        badgeContainer.appendChild(toggleBtn);
      }

      labelContainer.appendChild(badgeContainer);
    }

    bubble.appendChild(labelContainer);
  } else {
    // Non-leaf: use a badge container at bottom center (like leaf nodes)
    // This keeps share and toggle badges together
    const badgeContainer = document.createElement("div");
    badgeContainer.className = "bubble-badge-container nonleaf";

    // Reference node badge — distinct icon + state via shared helper.
    {
      const linked = getLinkedBadge(node);
      if (linked.isLinkedStub) {
        const refBadge = document.createElement("span");
        refBadge.className = `bubble-inline-badge ref ${linked.className}`;
        refBadge.textContent = linked.icon;
        refBadge.title = linked.title;
        badgeContainer.appendChild(refBadge);
        bubble.classList.add("reference-node");
        const stubClasses = linkedStubContainerClasses(node);
        if (stubClasses) bubble.className += ` ${stubClasses}`;
      }
    }

    // Notes badge - shows if node has notes
    if (node.notes && node.notes.trim()) {
      const notesBadge = document.createElement("span");
      notesBadge.className = "bubble-inline-badge notes";
      notesBadge.textContent = "\uD83D\uDDD2\uFE0F";
      // Hover preview is desktop-only.
      if (!isMobile()) {
        setupNotePreviewOnBadge(notesBadge, node.notes, callbacks?.getDocId?.(), callbacks?.getDek?.());
      }
      notesBadge.addEventListener("click", (e) => {
        e.stopPropagation();
        if (callbacks.onOpenModal) {
          callbacks.onOpenModal(node.id);
        }
      });
      badgeContainer.appendChild(notesBadge);
    }

    // Toggle button for nodes with children
    // Hide for read-only viewers
    if (hasChildren && !isViewer) {
      const toggleBtn = document.createElement("span");
      toggleBtn.className = "bubble-inline-badge toggle";
      toggleBtn.textContent = node.collapsed ? "+" : "\u2212";
      toggleBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const current = bubble._pNode.dataNode;
        current.collapsed = !current.collapsed;
        bubble.classList.toggle("collapsed", current.collapsed);
        toggleBtn.textContent = current.collapsed ? "+" : "\u2212";
        if (callbacks.onUpdateNode) {
          callbacks.onUpdateNode(node.id, { collapsed: current.collapsed });
        }
      });
      badgeContainer.appendChild(toggleBtn);
    }

    bubble.appendChild(badgeContainer);
    // The title is drawn on a canvas over the bubbles (titles.js).
  }
  } // end !canvasMode DOM construction

  // Initial positioning
  // Load position from localStorage (user-specific, not synced)
  // Coordinates are relative to the actual tree parent, including when it is
  // the focused root (which has no physics bubble). A move changes that frame.
  const parentId = parentPhys?.id || focused.id;
  const savedPos = savedPositionIn(options.savedPositions, node.id, parentId);
  let initialX = savedPos ? savedPos.x : 0;
  let initialY = savedPos ? savedPos.y : 0;

  if (!savedPos) {
    const siblings = (parentPhys ? parentPhys.dataNode.children : focused?.children) || [];
    const myIndex = siblings.findIndex((c) => c.id === node.id);
    const placedSiblings = siblings.some((c) => c.id !== node.id && savedPositionIn(options.savedPositions, c.id, parentId));
    if (myIndex >= 0 && placedSiblings) {
      // A new bubble among placed ones starts near their centre and is
      // pushed out to the nearest free space.
      const angle = (myIndex / siblings.length) * Math.PI * 2;
      const posRadius = parentPhys ? 60 : 100;
      initialX = Math.cos(angle) * posRadius;
      initialY = Math.sin(angle) * posRadius;
    } else if (myIndex >= 0) {
      // A group laid out for the first time starts on a sunflower spiral
      // spaced by its bubbles' size, so the simulation only has to settle
      // them (starting them all on a small ring made one deep pile that
      // burst apart, flinging some far away).
      let spacing = spiralSpacingCache.get(parentId);
      if (spacing === undefined) {
        const shown = siblings.filter((c) => isVisibleFn ? isVisibleFn(c) : true);
        const mean = shown.reduce((sum, c) => sum + layoutSize(c, calcSize(c, currentState.hideCompleted, minSize, padding, hiddenTagIds, nodeIsDoneFn, isVisibleFn)), 0) / (shown.length || 1);
        // Area per point of the spiral ≈ area per circle in a dense packing.
        spacing = 0.55 * (mean + SIBLING_GAP);
        spiralSpacingCache.set(parentId, spacing);
      }
      const angle = myIndex * GOLDEN_ANGLE;
      const dist = spacing * Math.sqrt(myIndex + 0.5);
      initialX = Math.cos(angle) * dist;
      initialY = Math.sin(angle) * dist;
    }
  }

  const pNode = {
    id: node.id,
    docId: currentDocId,
    parentId,
    el: bubble,
    centeredLabel,
    // The transform the kept element already has (physics.js updateBubblePos
    // writes only a moved one).
    _bx: keep ? previous._bx : undefined,
    _by: keep ? previous._by : undefined,
    x: initialX,
    y: initialY,
    vx: 0,
    vy: 0,
    radius: size / 2,
    bodyRadius: bodySize / 2,
    // Radius of a parent's title baseline, in the map's units (titles.js).
    titleRadius,
    // Space the title takes, so the layout keeps it clear (layout.js).
    title: isLeaf ? null : titleBandFor(node.name || node.text || "", size, titleRadius),
    parent: parentPhys,
    dataNode: node,
    routine,
    offsetX: offsetX,
    offsetY: offsetY,
    manuallyPositioned: !!savedPos,
    // Radius the saved position was converged/saved under — see
    // collectDirtyPhysicsIds. undefined for fresh nodes and pre-stamp entries.
    savedRadius: savedPos?.r,
    // Overlap with a sibling the saved layout already had (physics.js
    // saveAllPositions): accepted, only more than that is dirty.
    savedOverlap: savedPos?.o || 0,
    // Display data for the canvas renderer and the titles (titles.js).
    name: node.name || node.text || "",
    color: node.color,
    isLeaf,
    collapsedParent: !!(hasChildren && node.collapsed),
    status: bubbleStatus || null,
    spotlight,
  };
  physicsArray.push(pNode);
  if (bubble) {
    bubble._pNode = pNode;
    options.placed?.add(bubble);
  }

  if (bubble && !keep) {
  // Hover tracking
  bubble.addEventListener("mouseenter", (e) => {
    e.stopPropagation();
    state.setHoveredBubbleId(node.id);
  });
  bubble.addEventListener("mouseleave", (e) => {
    e.stopPropagation();
    if (state.getHoveredBubbleId() === node.id) {
      state.setHoveredBubbleId(null);
    }
  });
  } // end !canvasMode hover listeners

  // Recursively create children — same ancestor-aware visibility predicate
  // as the top-level filter, so a child whose own tag is hidden still
  // renders if its subtree contains a non-hidden descendant.
  if (hasChildren && !node.collapsed) {
    const childIsVisible = isVisibleFn || ((child) => {
      const localHiddenTagIds = callbacks?.getHiddenTagIds?.() || [];
      if (currentState.hideCompleted && (child.status === "done" || callbacks?.nodeIsDone?.(child))) return false;
      if (tagFilter()?.isHidden(child, localHiddenTagIds)) return false;
      return true;
    });
    const childrenToShow = node.children.filter(childIsVisible);
    childrenToShow.forEach((child) => {
      createBubbleRecursive(
        child,
        bubble,
        pNode,
        size / 2,
        size / 2,
        physicsArray,
        options,
      );
    });
  }

  if (keep) return; // placed above, its listeners already set

  if (bubble) {
  // Drag is mousedown-based, no-op on mobile so skip the listener.
  if (!isMobile()) {
    setupBubbleDrag(bubble, node, pNode, {
      state,
      physics,
    });
  }

  // Click to select (not the click ending a drag: interaction.js endGesture)
  bubble.addEventListener("click", (e) => {
    e.stopPropagation();
    if (bubble._dragGesture) {
      bubble._dragGesture = false;
      return;
    }
    if (callbacks.onNodeSelect) {
      callbacks.onNodeSelect(node.id);
    }
  });

  // Double-click to edit for leaf bubbles (prevents parent zoom)
  if (isLeaf) {
    bubble.addEventListener("dblclick", (e) => {
      e.stopPropagation();
      // Block inline editing for read-only viewers
      if (isViewer) return;
      if (callbacks.onNodeEdit) {
        callbacks.onNodeEdit(node.id, e.clientX, e.clientY);
      }
    });
  } else {
    // Double-click to zoom into (only for non-leaf nodes)
    bubble.addEventListener("dblclick", (e) => {
      e.stopPropagation();
      zoomTo(node.id);
    });
  }

  // Right click for context menu
  bubble.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (callbacks.onNodeContextMenu) {
      callbacks.onNodeContextMenu(node.id, e.clientX, e.clientY, bubble._pNode.dataNode);
    }
  });

  appendRoutineBadge(bubble,node.id);
  if (routine) fitRoutineNodeOrbit(bubble.querySelector(':scope > .routine-badge'), bodySize, bodySize, bodySize / 2);
  parentContainer.appendChild(bubble);
  } // end !canvasMode interaction + attach
}

// ============================================================================
// BREADCRUMBS
// ============================================================================

/**
 * Render breadcrumbs navigation
 * @param {Object} options - Render options
 * @param {HTMLElement} options.container - Container element
 * @param {Object} options.currentState - Current state
 * @param {Object} options.callbacks - Callbacks object
 * @param {Function} options.zoomTo - Zoom function
 */
export function renderBreadcrumbs(options) {
  const { container, currentState, callbacks, zoomTo } = options;

  const crumbsEl = container.querySelector("#b-crumbs");
  if (!crumbsEl) return;
  renderHorizonControl(container, currentState.horizon, callbacks.onHorizonChange, currentState.currentDocId);

  const path = [];
  let current = callbacks.find(currentState.focusId);

  while (current) {
    path.unshift(current);
    current = callbacks.findParent(current.id);
  }

  const escapeHtml = callbacks.escapeHtml || ((s) => s);

  const viewIndicator = `<span class="view-indicator">${viewLabel("bubble")}</span>`;

  crumbsEl.innerHTML =
    viewIndicator +
    path
      .map((node, i) => {
        const isLast = i === path.length - 1;
        const name = node.name || node.text || t("views.root");
        return `${i > 0 ? '<span class="crumb-sep">\u203A</span>' : ""}
                <div class="crumb ${isLast ? "current" : ""}" data-id="${escapeAttr(node.id)}">
                  <span class="crumb-dot" style="background:${safeColor(node.color)}"></span>${escapeHtml(name)}
                </div>`;
      })
      .join("");

  // Add click handlers to breadcrumbs
  crumbsEl.querySelectorAll(".crumb:not(.current)").forEach((crumb) => {
    crumb.addEventListener("click", () => {
      zoomTo(crumb.dataset.id);
    });
  });
}

// ============================================================================
// PROPERTY UPDATES
// ============================================================================

/**
 * Update bubble visual properties without full re-render
 * Called for property-only changes during physics to avoid interruption
 *
 * Handles: status, name, color, collapsed state, notes badge
 * Does NOT handle structural changes (those require full re-render)
 *
 * @param {Object} rootNode - Root node of the tree
 * @param {Object} state - State module accessors
 */
export function updateBubbleProperties(rootNode, state) {
  const world = state.getWorld();
  const callbacks = state.getCallbacks();

  if (!world || !rootNode) return;

  // Build a map of current node data for quick lookup
  const nodeMap = new Map();
  function buildMap(node) {
    if (!node) return;
    nodeMap.set(node.id, node);
    if (node.children) {
      node.children.forEach(buildMap);
    }
  }
  buildMap(rootNode);

  // Update each bubble's visual properties
  world.querySelectorAll(".bubble").forEach((bubble) => {
    const nodeId = bubble.dataset.id;
    const node = nodeMap.get(nodeId);
    if (!node) return;

    // Update status (derived from tags)
    const es = callbacks?.getEffectiveStatus?.(node) || node.status;
    if (es) {
      bubble.dataset.status = es;
    } else {
      delete bubble.dataset.status;
    }

    // Update color
    if (node.color) {
      bubble.style.outlineColor = node.color;
      bubble.style.setProperty("--bubble-ring-color", node.color);
      // For leaf nodes, also update the fill color
      if (bubble.classList.contains("leaf")) {
        bubble.style.setProperty("--bubble-fill-color", node.color);
        const textColor = getContrastColor(node.color);
        bubble.style.setProperty("--bubble-text-color", textColor);
      }
    }

    // Update collapsed state
    const hasChildren = node.children && node.children.length > 0;
    if (hasChildren) {
      bubble.classList.toggle("collapsed", !!node.collapsed);
      // Update toggle button text
      const toggleBtn = bubble.querySelector(".bubble-inline-badge.toggle");
      if (toggleBtn) {
        toggleBtn.textContent = node.collapsed ? "+" : "\u2212";
      }
    }

    // Update notes badge visibility
    const notesBadge = bubble.querySelector(".bubble-inline-badge.notes");
    if (notesBadge) {
      const hasNotes = node.notes && node.notes.trim();
      notesBadge.style.display = hasNotes ? "" : "none";
      if (hasNotes) {
        setupNotePreviewOnBadge(notesBadge, node.notes, callbacks?.getDocId?.(), callbacks?.getDek?.());
      }
    }

    // Update priority indicator
    const priorityIndicator = bubble.querySelector(".bubble-inline-badge.priority");
    if (priorityIndicator) {
      if (node.priority) {
        priorityIndicator.dataset.priority = node.priority;
        priorityIndicator.style.display = "";
      } else {
        priorityIndicator.style.display = "none";
      }
    }

    // Update dates badge
    const datesBadge = bubble.querySelector(".bubble-inline-badge.dates");
    if (datesBadge) {
      const hasDates = node.startDate || node.endDate;
      datesBadge.style.display = hasDates ? "" : "none";
      if (hasDates) {
        datesBadge.title = `${node.startDate || "?"} - ${node.endDate || "?"}`;
      }
    }

    // Update name in leaf labels
    const leafLabel = bubble.querySelector(".bubble-leaf-label");
    if (leafLabel) {
      const newName = node.name || node.text || "";
      if (leafLabel.textContent !== newName) {
        leafLabel.textContent = newName;
        leafLabel.title = newName;
      }
    }

    // computeTreeStructureHash excludes _linkedState, so partial updates must re-derive the badge.
    const refBadge = bubble.querySelector(".bubble-inline-badge.ref");
    const linked = getLinkedBadge(node);
    if (refBadge && linked.isLinkedStub) {
      const expectedClass = `bubble-inline-badge ref ${linked.className}`;
      if (refBadge.className !== expectedClass) refBadge.className = expectedClass;
      if (refBadge.textContent !== linked.icon) refBadge.textContent = linked.icon;
      if (refBadge.title !== linked.title) refBadge.title = linked.title;
      const stubClasses = linkedStubContainerClasses(node) || "";
      bubble.classList.forEach((cls) => {
        if (cls.startsWith("linked-stub")) bubble.classList.remove(cls);
      });
      if (stubClasses) {
        for (const cls of stubClasses.split(/\s+/).filter(Boolean)) {
          bubble.classList.add(cls);
        }
      }
    }
  });
}
